import type { FormTable } from "@/types/forms/form";
import { type SidebarDefinition, defineSidebar } from "@/types/sidebar-session";
import type { UserTiny } from "@/types/user";

import { useRightSidebarStore } from "@/stores/right-sidebar-store";

/** The root sidebar owns this handoff after the page's opener unmounts. */
export interface FormSidebarPayload {
  form: Pick<FormTable, "id" | "name" | "numSubmissions">;
  patient?: UserTiny;
  submission?: { isCompleted: boolean; hadUploadError?: boolean };
  answeredFormId?: string;
  /** Only completed, unexpired clinical submissions may offer this action. */
  onFillAgain?: () => void;
  /** The page that owns the refill dialog and supplied its callback. */
  fillAgainPath?: string;
}

export enum FormSidebarCommand {
  RECONCILE_PATH = "RECONCILE_PATH",
}

type FormSidebarInput =
  | FormSidebarPayload
  | { command: FormSidebarCommand.RECONCILE_PATH; path: string };

export const formSidebarDefinition: SidebarDefinition<
  FormSidebarInput,
  FormSidebarPayload
> = defineSidebar<FormSidebarInput, FormSidebarPayload>({
  id: "FORM",
  label: "Form",
  order: 50,
  isAvailable: (environment) => environment.ownerScope !== null,
  strategy: {
    planOpen(context, input) {
      if ("command" in input) {
        const existing = context.read(formSidebarDefinition);
        if (
          !existing?.payload.onFillAgain ||
          existing.payload.fillAgainPath === input.path
        ) {
          return {};
        }
        // Page-owned callbacks must not revive after leaving and returning.
        // Keep the viewer and the selected sibling, but retire this capability.
        return {
          upsert: [
            context.write(formSidebarDefinition, {
              ...existing.payload,
              onFillAgain: undefined,
              fillAgainPath: undefined,
            }),
          ],
        };
      }
      return {
        upsert: [context.write(formSidebarDefinition, input)],
        select: formSidebarDefinition.id,
      };
    },
  },
});

export function reconcileFormSidebarPath(path: string) {
  useRightSidebarStore.getState().open(formSidebarDefinition, {
    command: FormSidebarCommand.RECONCILE_PATH,
    path,
  });
}
